import type { JobOccurrenceCatalogDto, JobOccurrenceDto } from '@/api/generator';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import { getDefaultContextMenuActions } from '@/components/ContextMenu/utils';
import { routenames } from '@/router/routenames';
import { mdiDownload, mdiOpenInNew } from '@mdi/js';
import { api } from '@/api';
import type { Router } from 'vue-router';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { ConfirmationModalOptions } from '@/components/confirmationModals/types';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';

function downloadOrPrint(router: Router, id: string) {
  const printUrl = router.resolve({
    name: routenames.jobOccurrences.report,
    params: { id },
  }).href;

  window.open(printUrl, '_blank');
}

// only the id is needed, so this works for list items and for the full job occurrence
export async function deleteJobOccurrence(
  jobOccurrence: Pick<JobOccurrenceDto, 'id'>,
  onSuccess?: () => void | Promise<void>,
) {
  const { success } = await api.jobOccurrencesApi.deleteJobOccurrence({
    id: jobOccurrence.id,
  });

  if (success && onSuccess) {
    await onSuccess();
  }

  return success;
}

type DeleteAction = (args: {
  param: JobOccurrenceCatalogDto;
  confirmationOptions: ConfirmationModalOptions;
}) => Promise<{ confirmed: true; result: void } | { confirmed: false; result: undefined }>;

export function getJobOccurrenceContextMenuActions(
  item: JobOccurrenceCatalogDto,
  router: Router,
  t: any,
  deleteAction: DeleteAction,
): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);

  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.jobOccurrences.execution,
        params: {
          id: item.id,
        },
        hash: `#${TAB_PREFIX}workitems`,
      },
    },
    {
      text: t('OpenJobInNewTab'),
      icon: mdiOpenInNew,
      callback: () => {
        const url = router.resolve({
          name: routenames.jobOccurrences.execution,
          params: {
            id: item.id,
          },
          hash: `#${TAB_PREFIX}workitems`,
        }).href;
        window.open(url, '_blank');
      },
    },
    {
      text: t('DownloadOrPrintReport'),
      icon: mdiDownload,
      callback: () => {
        downloadOrPrint(router, item.id);
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const defaultConfirmationOptions = getDefaultDeleteConfirmationOptions(
          {
            id: item.id,
            name: item.jobConfigurationName ?? '',
            typeName: 'Job_one',
            iuid: item.jobConfigurationIuid,
          },
          t,
        );
        await deleteAction({
          param: item,
          confirmationOptions: {
            ...defaultConfirmationOptions,
            contentData: {
              ...defaultConfirmationOptions.contentData,
              hint: t('DeleteJobOccurrenceHintMsg', {
                hint: defaultConfirmationOptions.contentData.hint,
              }),
            },
          },
        });
      },
    },
  ];
}
